import { create } from "zustand";
import { useVisitStore } from "./visitStore";
import { useVisitTokenStore } from "./visitTokenStore";
import { PackageDepartmentEnum } from "@/__generated__/graphql";

export type RescheduleVisitWorkerType = "CURRENT_WORKER" | "ANY_WORKER";

type VisitDetailState = {
  showModal: boolean;
  showRateVisitModal: boolean;
  showSkipVisitModal: boolean;
  showRescheduleVisitConfirmation: boolean;
  showContactSupportToRescheduleModal: boolean;
  showSkipVisitSurveySuccess: boolean;
  initialRate?: number;
  visitId: string | null;
  visitToken: string | null;
  rescheduleVisitWorkerType: RescheduleVisitWorkerType | null;
  visitDepartment: PackageDepartmentEnum | null;
};

type VisitDetailStore = {
  data: VisitDetailState;
  openVisitModal(visitId: string, token?: string): void;
  closeVisitModal(): void;
  toggleVisitModal(): void;
  openRateVisitModalById(visitId: string, rate?: number): void;
  closeRateVisitModal(): void;
  openSkipVisitModalById(visitId: string): void;
  closeSkipVisitModal(): void;
  openRescheduleVisitConfirmation(visitId: string, token?: string): void;
  closeRescheduleVisitConfirmation(): void;
  openContactSupportToRescheduleModal(): void;
  closeContactSupportToRescheduleModal(): void;
  setRescheduleVisitWorkerType(type: RescheduleVisitWorkerType | null): void;
  setVisitDepartment(visitDepartment: PackageDepartmentEnum | null): void;
  openSkipVisitConfirmationModal(): void;
  closeSkipVisitConfirmationModal(): void;
  openSkipVisitSurveySuccessModal(): void;
  closeSkipVisitSurveySuccessModal(): void;
};

const initialData: VisitDetailState = {
  showModal: false,
  showRateVisitModal: false,
  showSkipVisitModal: false,
  showContactSupportToRescheduleModal: false,
  showRescheduleVisitConfirmation: false,
  showSkipVisitSurveySuccess: false,
  initialRate: undefined,
  visitId: null,
  visitToken: null,
  rescheduleVisitWorkerType: null,
  visitDepartment: null,
};

export const useVisitDetailStore = create<VisitDetailStore>((set, get) => ({
  data: initialData,
  openVisitModal: (visitId: string, token?: string) => {
    set({
      data: {
        ...get().data,
        showModal: true,
        visitId,
        visitToken: token ?? null,
      },
    });

    if (token) {
      useVisitTokenStore.getState().fetch({
        requestPayload: {
          token,
        },
      });
    } else {
      useVisitStore.getState().fetch({
        requestPayload: {
          id: visitId,
        },
      });
    }
  },
  closeVisitModal: () => {
    const currentData = get().data;
    set({
      data: {
        ...currentData,
        showModal: false,
        showRateVisitModal: false,
        showSkipVisitModal: false,
        showContactSupportToRescheduleModal: false,
        showRescheduleVisitConfirmation: false,
        showSkipVisitSurveySuccess: false,
        initialRate: undefined,
        visitId: null,
        visitToken: null,
        rescheduleVisitWorkerType: null,
      },
    });
  },
  toggleVisitModal: () => {
    const currentData = get().data;

    set({
      data: {
        ...currentData,
        showModal: !currentData.showModal && currentData.visitId !== null,
      },
    });
  },
  openRateVisitModalById: (visitId: string, rate?: number) => {
    set({
      data: {
        ...get().data,
        showRateVisitModal: true,
        visitId,
        initialRate: rate,
      },
    });
  },
  closeRateVisitModal: () => {
    set({
      data: {
        ...get().data,
        showRateVisitModal: false,
      },
    });
  },
  openSkipVisitModalById: (visitId: string) => {
    set({
      data: {
        ...get().data,
        showRescheduleVisitConfirmation: false,
        showSkipVisitModal: true,
        visitId,
      },
    });
  },
  closeSkipVisitModal: () => {
    set({
      data: {
        ...get().data,
        showSkipVisitModal: false,
      },
    });
  },
  openRescheduleVisitConfirmation: (visitId: string, token?: string) => {
    set({
      data: {
        ...get().data,
        showRescheduleVisitConfirmation: true,
        showSkipVisitModal: false,
        visitId,
        visitToken: token ?? null,
      },
    });

    if (token) {
      useVisitTokenStore.getState().fetch({
        requestPayload: {
          token,
        },
      });
    } else {
      useVisitStore.getState().fetch({
        requestPayload: {
          id: visitId,
        },
      });
    }
  },
  closeRescheduleVisitConfirmation: () => {
    set({
      data: {
        ...get().data,
        showRescheduleVisitConfirmation: false,
        showModal: false,
      },
    });
  },
  openContactSupportToRescheduleModal: () => {
    set({
      data: {
        ...get().data,
        showContactSupportToRescheduleModal: true,
      },
    });
  },
  closeContactSupportToRescheduleModal: () => {
    set({
      data: {
        ...get().data,
        showContactSupportToRescheduleModal: false,
      },
    });
  },
  setRescheduleVisitWorkerType: (type: RescheduleVisitWorkerType | null) => {
    set({
      data: {
        ...get().data,
        rescheduleVisitWorkerType: type,
      },
    });
  },
  setVisitDepartment: (department: PackageDepartmentEnum | null) => {
    set({
      data: {
        ...get().data,
        visitDepartment: department,
      },
    });
  },
  openSkipVisitConfirmationModal: () => {
    set({
      data: {
        ...get().data,
      },
    });
  },
  closeSkipVisitConfirmationModal: () => {
    set({
      data: {
        ...get().data,
      },
    });
  },
  openSkipVisitSurveySuccessModal: () => {
    set({
      data: {
        ...get().data,
        showSkipVisitSurveySuccess: true,
      },
    });
  },
  closeSkipVisitSurveySuccessModal: () => {
    set({
      data: {
        ...get().data,
        showSkipVisitSurveySuccess: false,
      },
    });
  },
}));
